/**
 * First-interaction detection: "has a human touched this page yet?"
 *
 * We had no way to answer that. PostHog runs with `autocapture: false` (see
 * posthog.ts) so no click ever reaches it on its own, and every other signal we
 * collect - pageview, web vitals, pageleave, time on page - is produced just as
 * readily by a machine loading the page.
 *
 * That gap has a cost. A Microsoft Defender Safe Links sandbox opened four links
 * from one day-13 trial email inside the same second (CODEPULSE-BACKEND-2ZY), and
 * each one loaded gtag's Google Ads remarketing ping and the LinkedIn Insight
 * Tag, so phantom visits entered ad audiences and fed view-through bidding. Dwell
 * time cannot separate those from a human: that sandbox sat on /pricing for over
 * 15 seconds - long enough to trip the stuck-spinner watchdog.
 *
 * Interaction can. A scanner loads, renders, screenshots and dies; it does not
 * move a pointer, press a key, scroll or touch.
 */

// Deliberately NOT 'scroll': that fires for PROGRAMMATIC scrolling too - our own
// ScrollToTop calls window.scrollTo (including a smooth-behaviour animation for
// hash targets, which emits a burst of them), so a machine could be credited with
// a human scroll it never made. Every real way to scroll is already covered:
// wheel (mouse/trackpad), touchstart (touch), keydown (space/arrows/PgDn),
// pointerdown (dragging the scrollbar).
//
// 'pointermove' is here because clicking and scrolling turned out to be too high
// a bar: of 74 /pricing sessions in 30 days only 34 produced any click or a
// scroll past 25% (the scroll_depth threshold), so desktop readers who just
// looked and left were being left out of ad audiences on the page that sells.
// Moving a cursor is evidence a person is present, and a load-render-screenshot
// sandbox produces none of it. Strictly it says "a human is here" rather than "a
// human acted" - a stationary cursor with the page moving under it can emit one -
// which is the right bar for this purpose.
const INTERACTION_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel'] as const;

let interacted = false;
let listening = false;
const callbacks = new Set<() => void>();

function handleInteraction(): void {
  if (interacted) return;
  interacted = true;
  stopListening();

  // Copied before iterating: a callback may register another one.
  const pending = Array.from(callbacks);
  callbacks.clear();
  for (const cb of pending) {
    try {
      cb();
    } catch {
      // A failing subscriber must not stop the others from running.
    }
  }
}

function startListening(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  for (const type of INTERACTION_EVENTS) {
    // Capture phase so a stopPropagation() anywhere in the tree cannot hide a
    // real interaction from us; passive so we never delay scrolling.
    window.addEventListener(type, handleInteraction, { capture: true, passive: true });
  }
}

function stopListening(): void {
  if (!listening || typeof window === 'undefined') return;
  listening = false;
  for (const type of INTERACTION_EVENTS) {
    window.removeEventListener(type, handleInteraction, { capture: true });
  }
}

// Listen from module evaluation, NOT from the first subscriber. analytics.init()
// runs in an App effect, and the landing pageview already races it (see the
// pendingPage buffer in index.ts) - so a visitor can click or scroll before any
// subscriber exists. Waiting for one would throw that interaction away and leave
// a real person looking exactly like a scanner until they touched the page again.
startListening();

/** True once a pointer, key, touch or scroll event has been seen. */
export function hasInteracted(): boolean {
  return interacted;
}

/**
 * Run `callback` on the first human interaction, or immediately if one already
 * happened. Callbacks run at most once each.
 */
export function onFirstInteraction(callback: () => void): void {
  if (interacted) {
    callback();
    return;
  }
  callbacks.add(callback);
  startListening();
}

/**
 * Test-only: forget that an interaction happened, drop all subscribers, and
 * re-arm the listeners (matching the state a fresh module import would be in).
 */
export function resetInteractionState(): void {
  stopListening();
  interacted = false;
  callbacks.clear();
  startListening();
}
